<template>
  <div>
    <v-row no-gutters align="center" class="mb-3">
      <v-card outlined class="pa-3 flex-grow-1 mr-3">
        <div class="kpi-label">{{ $t("finance_model_label") }}</div>
        <div class="kpi-value">{{ paymentModelSummary }}</div>
      </v-card>
      <v-card outlined class="pa-3 flex-grow-1 mr-3">
        <div class="kpi-label">{{ $t("finance_total_charged") }}</div>
        <div class="kpi-value">{{ formatCurrency(totalCharged) }}</div>
      </v-card>
      <v-card outlined class="pa-3 flex-grow-1">
        <div class="kpi-label">{{ $t("finance_total_paid") }}</div>
        <div class="kpi-value">{{ formatCurrency(totalPaid) }}</div>
      </v-card>
      <primary-button v-if="isAdmin" class="ml-3" @click="dialogOpen = true">
        <v-icon left small>mdi-plus</v-icon>
        {{ $t("finance_new_platform_payment") }}
      </primary-button>
    </v-row>

    <!-- Zaduženje (SUBSCRIPTION) - koliko Ordera potražuje od restorana, odvojeno
         od stvarnih uplata ispod. Backend odgovor 16.09.2026 §3.2 - stariji
         odgovor je slao samo jednu (pogrešno imenovanu) listu payments. -->
    <p class="section-label">{{ $t("finance_charges_label") }}</p>
    <v-card outlined class="mb-4">
      <v-data-table
        :headers="chargeHeaders"
        :items="charges"
        :loading="loading"
        :loading-text="$t('finance_loading_payments')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_payment_items')"
      >
        <template #item.period="{ item }">
          <span class="font-weight-bold">{{ item.period }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
        <template #item.rateValue="{ item }">
          <span v-if="item.rateValue != null">{{ item.rateValue }}</span>
          <span v-else>—</span>
        </template>
        <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
      </v-data-table>
    </v-card>

    <p class="section-label">{{ $t("finance_payments_received_label") }}</p>
    <v-card outlined>
      <v-data-table
        :headers="headers"
        :items="payments"
        :loading="loading"
        :loading-text="$t('finance_loading_payments')"
        mobile-breakpoint="0"
        :no-data-text="$t('finance_no_payment_items')"
        @click:row="openPayment"
      >
        <template #item.period="{ item }">
          <span class="font-weight-bold">{{ item.period }}</span>
        </template>
        <template #item.amount="{ item }">{{ formatCurrency(item.amount) }}</template>
        <template #item.date="{ item }">{{ localeDate(item.date) }}</template>
      </v-data-table>
    </v-card>

    <platform-payment-detail-dialog v-model="detailOpen" :payment="selectedPayment" />

    <platform-payment-form-dialog
      v-if="isAdmin"
      v-model="dialogOpen"
      :saving="saving"
      @save="onSavePayment"
    />
  </div>
</template>

<script>
import restaurantFinanceService from "@/services/restaurantFinance";
import hasSnackbar from "@/mixins/hasSnackbar";
import PlatformPaymentDetailDialog from "./dialogs/PlatformPaymentDetailDialog.vue";
import PlatformPaymentFormDialog from "./dialogs/PlatformPaymentFormDialog.vue";

const mapPaymentDto = (dto) => ({
  id: dto.id,
  amount: Number(dto.amount) || 0,
  period: dto.period,
  note: dto.note,
  date: dto.created_at,
});

// charges (zaduženje/SUBSCRIPTION) nose i rate_value (stopa pretplate na tom
// redu) - backend odgovor 16.09.2026 §3.2, isto polje kao na /transactions.
const mapChargeDto = (dto) => ({
  id: dto.id,
  amount: Number(dto.amount) || 0,
  period: dto.period,
  note: dto.note,
  rateValue: dto.rate_value != null ? Number(dto.rate_value) : null,
  date: dto.created_at,
});

export default {
  name: "FinanceTabPlatformPayments",

  components: { PlatformPaymentDetailDialog, PlatformPaymentFormDialog },

  mixins: [hasSnackbar],

  data: () => ({
    paymentModel: null,
    totalPaid: 0,
    totalCharged: 0,
    payments: [],
    charges: [],
    loading: true,
    saving: false,
    detailOpen: false,
    selectedPayment: null,
    dialogOpen: false,
  }),

  computed: {
    restaurantId() {
      return this.$store.state.restaurant?.id;
    },

    isAdmin() {
      return this.$store.state.user?.user?.type === "admin";
    },

    headers() {
      return [
        { text: this.$t("finance_period_label"), value: "period" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_date"), value: "date" },
      ];
    },

    chargeHeaders() {
      return [
        { text: this.$t("finance_period_label"), value: "period" },
        { text: this.$t("finance_amount"), value: "amount", align: "end" },
        { text: this.$t("finance_rate_label"), value: "rateValue", align: "end" },
        { text: this.$t("finance_date"), value: "date" },
      ];
    },

    // paying_type/paying_month/paying_percent/paying_meal su postojeća polja
    // na restoranu (ista logika kao payingTypeOptions u RestaurantSettingsDialog).
    paymentModelSummary() {
      const model = this.paymentModel;
      const type = model ? Number(model.paying_type) : null;
      if (type === 1) return `${this.$t("by_month")}: ${this.formatCurrency(model.paying_month)}`;
      if (type === 2) return `${this.$t("by_percent")}: ${model.paying_percent}%`;
      if (type === 3) return `${this.$t("by_meal")}: ${this.formatCurrency(model.paying_meal)}`;
      return this.$t("finance_payment_model_not_set");
    },
  },

  created() {
    this.loadPayments();
  },

  methods: {
    async loadPayments() {
      if (!this.restaurantId) return;

      this.loading = true;
      try {
        const { data } = await restaurantFinanceService.getPlatformPayments(this.restaurantId);
        const payload = data?.data || {};
        this.paymentModel = payload.payment_model || null;
        this.totalPaid = Number(payload.total_paid) || 0;
        this.totalCharged = Number(payload.total_charged) || 0;
        this.payments = (payload.payments || []).map(mapPaymentDto);
        this.charges = (payload.charges || []).map(mapChargeDto);
      } catch (error) {
        this.showErrorSnackbar(
          error?.response?.data?.message || this.$t("finance_platform_payments_load_error")
        );
      } finally {
        this.loading = false;
      }
    },

    openPayment(payment) {
      this.selectedPayment = payment;
      this.detailOpen = true;
    },

    async onSavePayment(payload) {
      this.saving = true;
      try {
        await restaurantFinanceService.createPlatformPayment(this.restaurantId, payload);
        this.showSuccessSnackbar(this.$t("finance_payment_saved"));
        this.dialogOpen = false;
        await this.loadPayments();
      } catch (error) {
        this.showErrorSnackbar(this.$t("finance_payment_save_error"));
      } finally {
        this.saving = false;
      }
    },

    formatCurrency(amount) {
      const value = Number(amount) || 0;
      return `${value.toFixed(2)} ${this.restaurantCurrency}`;
    },
  },
};
</script>

<style scoped>
.kpi-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.5);
}

.kpi-value {
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 4px;
}

.section-label {
  margin: 0 0 8px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #9aa4b2;
}

::v-deep tbody tr {
  cursor: pointer;
}
</style>
